import { array } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import React, { useState } from "react";
import { TCaseQuoteAddAdditionalItemForm } from "../../../../../domain/codecs/form/CaseForm";
import { TQuoteAdditionalItemOption } from "../../../../../domain/codecs/Quote";
import { fromNumber } from "../../../../../domain/models/TCurrency";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Padding } from "../../BuildingBlocks/Padding";
import { Relative } from "../../BuildingBlocks/Relative";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMBottomControlBar } from "../../CRMBottomControlBar/CRMBottomControlBar";
import { CRMButtonPrimary } from "../../CRMButtonPrimary/CRMButtonPrimary";
import { CRMDropdownComponent } from "../../CRMDropdownComponent/CRMDropdownComponent";
import { CRMFormButton } from "../../CRMFormButton/CRMFormButton";
import CRMInputLabelAndErrorWrapComponent from "../../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { CRMPopoutHeader } from "../../Simple/CRMPopoutHeader/CRMPopoutHeader";
import { CRMTidyPopout } from "../../Simple/CRMTidyPopout/CRMTidyPopout";
import { CRMEmptyPlaceholder } from "../../Simple/CRMEmptyPlaceholder/CRMEmptyPlaceholder";
import { WeightMedium } from "../../WeightMedium/WeightMedium";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";

export const CRMQuoteAddItemPopout = (props: React.PropsWithChildren<{
    form: TCaseQuoteAddAdditionalItemForm;
    options: Array<TQuoteAdditionalItemOption>;
    onClose: () => void;
    onChange: (form: TCaseQuoteAddAdditionalItemForm) => void;
    onSave: (form: TCaseQuoteAddAdditionalItemForm) => void;
}>): JSX.Element => {
    const [displayError, setDisplayError] = useState<boolean>(false);

    const hasOptions = props.options.length > 0;

    const getSelectedOption = () =>
        pipe(
            props.options,
            array.findFirst(({quote_rate_id}) => quote_rate_id === props.form.edited.quote_rate_id),
        );

    const getSelectedPrice = () =>
        pipe(
            getSelectedOption(),
            (option) => option._tag === "Some"
                ? fromNumber(option.value.fee_pence_total / 100, 2)
                : null,
        );

    const onSave = () => {
        if (props.form.edited.quote_rate_id === "") {
            setDisplayError(true);
        } else {
            setDisplayError(false);
            props.onSave(props.form);
        }
    };

    return (
        <CRMTidyPopout width="100%" height="730px">
            <Relative>
                <CRMPopoutHeader
                    icon="arrow-left"
                    onClick={props.onClose}
                >
                    Add an item
                </CRMPopoutHeader>

                <Padding spacing={CRMSpacing.LARGE}>
                    {!hasOptions &&
                        <CRMEmptyPlaceholder
                            iconSize="70px"
                            textGap={CRMSpacing.TINY}
                        >
                            There are no items left to add to this quote.
                        </CRMEmptyPlaceholder>
                    }

                    {hasOptions &&
                        <SpacingColumn spacing={CRMSpacing.LARGE}>
                            <CRMInputLabelAndErrorWrapComponent
                                label="Which item do you want to add?"
                                displayError={displayError}
                                errorMessage="Choose an item to add."
                            >
                                <CRMDropdownComponent
                                    value={props.form.edited.quote_rate_id}
                                    displayError={displayError}
                                    placeholder="Choose an item"
                                    options={pipe(
                                        props.options,
                                        array.map((option) => ({
                                            value: option.quote_rate_id,
                                            label: option.item_display_name,
                                        })),
                                    )}
                                    onChange={(quote_rate_id) => props.onChange({
                                        ...props.form,
                                        edited: {
                                            ...props.form.edited,
                                            quote_rate_id,
                                        },
                                    })}
                                />
                            </CRMInputLabelAndErrorWrapComponent>

                            {getSelectedPrice() !== null &&
                                <CRMParagraph>
                                    <WeightSemiBold>
                                        Adds {getSelectedPrice()} including VAT.
                                    </WeightSemiBold>

                                    <WeightMedium>
                                        &nbsp;This writes a new quote and keeps the current one in the history.
                                    </WeightMedium>
                                </CRMParagraph>
                            }
                        </SpacingColumn>
                    }
                </Padding>

                {hasOptions &&
                    <CRMBottomControlBar>
                        <Padding
                            type="custom"
                            width="100%"
                            spacing={`${CRMSpacing.TINY} ${CRMSpacing.MEDIUM}`}
                        >
                            <SpacingRow justifyContent="end">
                                <CRMFormButton
                                    formStatus={props.form.status}
                                    ButtonElement={CRMButtonPrimary}
                                    label="Add to quote"
                                    onClick={onSave}
                                />
                            </SpacingRow>
                        </Padding>
                    </CRMBottomControlBar>
                }
            </Relative>
        </CRMTidyPopout>
    );
};
